<script setup lang="ts">
import { onMounted, ref } from 'vue';
import SelectUserOverlay from '@/overlays/SelectUserOverlay.vue';
import EnterPinOverlay from '@/overlays/EnterPinOverlay.vue';
import { IDepartment, IProfile } from '@/interfaces';
import {
  DepartmentFragment,
  MealParticipantFragment,
} from '@/graphql/custom-types';

interface Props {
  title: string;
  titleBold?: string;
  participating?: boolean;
  multiSelect?: boolean;
  sharedDepartments?: (IDepartment | DepartmentFragment)[];
  participants?: (IProfile | MealParticipantFragment)[];
  employeeOnly?: boolean;
  limitedToProfile?: IProfile | undefined;
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  participating: true,
  multiSelect: false,
  employeeOnly: false,
});

onMounted(() => {
  selectedProfile.value = props.limitedToProfile || undefined;
  if (!selectedProfile.value) {
    showSelectUserOverlay.value = true;
  } else {
    showEnterPinOverlay.value = true;
  }
});

const emit = defineEmits<{ authenticated: [IProfile, string]; close: [] }>();

const showSelectUserOverlay = ref<boolean>(false);
const showEnterPinOverlay = ref<boolean>(false);
const selectedProfile = ref<IProfile>();

const profileSelected = (profile: IProfile) => {
  selectedProfile.value = profile;
  showEnterPinOverlay.value = true;
};

const handlePin = (pin: string) => {
  emit('authenticated', selectedProfile.value!, pin);
};

const closeShowEnterPinFlow = () => {
  showEnterPinOverlay.value = false;
  // If only one user is allowed close all overlays, otherwise only close enter pin overlay
  if (!props.limitedToProfile) {
    selectedProfile.value = undefined;
    return;
  }

  emit('close');
};
</script>

<template>
  <SelectUserOverlay
    v-if="showSelectUserOverlay"
    :participating="participating"
    :multi-select="multiSelect"
    :participants="participants"
    :shared-departments="sharedDepartments"
    :employee-only="employeeOnly"
    @close="$emit('close')"
    @profile-selected="profileSelected"
  />

  <EnterPinOverlay
    v-if="showEnterPinOverlay && selectedProfile"
    :title="title"
    :title-bold="titleBold"
    :profile="selectedProfile"
    :user-specific="limitedToProfile !== undefined"
    :loading="loading"
    @close="closeShowEnterPinFlow"
    @success="handlePin"
  />
</template>
